import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { paymentAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { CheckCircle, Loader2 } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await paymentAPI.getStripeStatus(sessionId);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setPaymentData(data);
        setStatus('success');
        clearCart(); // Clear cart on successful payment
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Errore verifica pagamento:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-secondary/20">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold mb-2">Verifica Pagamento</h1>
              <p className="text-muted-foreground">
                Stiamo verificando il tuo pagamento...
              </p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Pagamento Completato!</h1>
              <p className="text-muted-foreground mb-6">
                Grazie per il tuo acquisto. Riceverai una email di conferma a breve.
              </p>
              <div className="space-y-3">
                <Button className="w-full" asChild>
                  <Link to="/dashboard">Vai ai tuoi Ordini</Link>
                </Button>
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/">Torna alla Home</Link>
                </Button>
              </div>
            </>
          )}
          
          {status === 'timeout' && (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-100 mx-auto mb-4 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-yellow-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Verifica in Corso</h1>
              <p className="text-muted-foreground mb-6">
                La verifica del pagamento sta richiedendo più tempo del previsto. 
                Controlla la tua email per la conferma.
              </p>
              <Button asChild>
                <Link to="/">Torna alla Home</Link>
              </Button>
            </>
          )}
          
          {(status === 'error' || status === 'expired') && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
                <span className="text-3xl">❌</span>
              </div>
              <h1 className="text-2xl font-bold mb-2">
                {status === 'expired' ? 'Sessione Scaduta' : 'Errore'}
              </h1>
              <p className="text-muted-foreground mb-6">
                {status === 'expired' 
                  ? 'La sessione di pagamento è scaduta. Riprova.'
                  : 'Si è verificato un errore durante la verifica del pagamento.'
                }
              </p>
              <Button asChild>
                <Link to="/">Torna alla Home</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
